'use client';
import React from 'react';
import dayjs from 'dayjs';
import { cn } from '@shadcn/lib/utils';
import { useTranslations, useLocale } from 'next-intl';
import type { Lecturer, Courseversion } from '@/payload/payload-types';
import { useCourseVersion } from '../../_providers/CourseVersion';
import { formatPrice } from '../../_utilities/format-price';
import { ScrollArea } from '@radix-ui/react-scroll-area';
import { getDiscountReduction, useTrackEvent } from '../../_providers/TrackEventProvider';
import { logToPayload } from '../../_utilities/log-to-payload';

type CourseVersionTitleProps = {
	courseVersion: Courseversion;
	className?: string;
};

export const CourseVersionTitle: React.FC<CourseVersionTitleProps> = ({
	courseVersion,
	className,
}) => {
	const leadingLecturer = courseVersion.lecturers.find(_lecturer => _lecturer.isLeading);
	const lecturer = leadingLecturer?.lecturer as Lecturer;
	return (
		<p
			className={cn('text-sm font-medium', className)}
		>{`${dayjs(courseVersion.timeEdit.startDate).format('DD.MM')} - ${dayjs(courseVersion.timeEdit.endDate).format('DD.MM')} | ${lecturer?.name}`}</p>
	);
};

type CourseVersionSubtitleProps = {
	courseVersion: Courseversion;
	className?: string;
};

export const CourseVersionSubTitle: React.FC<CourseVersionSubtitleProps> = ({
	courseVersion,
	className,
}) => {
	const t = useTranslations('Course');
	const locale = useLocale();
	return (
		<p
			className={cn('text-[10px] font-bold uppercase', className)}
		>{`${t('courseversion-mode-short', { mode: courseVersion.mode })} ${formatPrice(courseVersion.discountedPrice || courseVersion.price, locale)}`}</p>
	);
};

export const CourseVersionSelect: React.FC<{ className?: string }> = ({ className }) => {
	const { selectedCourseVersion, setSelectedCourseVersion, allCourseVersions } =
		useCourseVersion();
	const trackEvent = useTrackEvent();

	if (allCourseVersions.length < 2) return null;

	return (
		<ScrollArea
			className={cn(
				'border-gold-light h-fit max-h-[238px] w-full overflow-auto rounded-2xl border p-1 lg:w-fit lg:max-w-[752px]',
				className,
			)}
		>
			<div className="flex flex-col lg:flex-row">
				{allCourseVersions.map((courseVersion: Courseversion) => {
					if (typeof courseVersion.course === 'string') {
						logToPayload(
							'Bad depth for courseversion.course at <CourseVersionSelect/>',
							{ courseId: courseVersion.course, courseversionId: courseVersion.id },
						);
					}
					return (
						<a
							data-fullstory="course-version-select"
							className={`flex cursor-pointer flex-col rounded-xl px-6 py-4 whitespace-nowrap ${courseVersion.id === selectedCourseVersion?.id ? 'bg-gold-light text-white' : 'text-navy'}`}
							onClick={e => {
								e.preventDefault();
								setSelectedCourseVersion(courseVersion);
								trackEvent('course_version_clicked', {
									course_name:
										typeof courseVersion.course !== 'string'
											? courseVersion.course.title
											: '',
									course_discount_value_eur:
										getDiscountReduction(selectedCourseVersion),
								});
							}}
							key={courseVersion.id}
						>
							<CourseVersionTitle courseVersion={courseVersion} />
							<CourseVersionSubTitle courseVersion={courseVersion} />
						</a>
					);
				})}
			</div>
		</ScrollArea>
	);
};
